import { Search, Bell, HelpCircle, ChevronDown, LogOut, User, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { XeroLogo } from "@/components/XeroLogo";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export const TopBar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const iconBtn =
    "relative flex items-center justify-center h-11 w-11 rounded-full text-slate-500 hover:text-[#13B5EA] hover:bg-[#e8f7fc] transition-colors";

  return (
    <header
      className="h-20 shrink-0 bg-white border-b border-slate-200 flex items-center justify-between px-6"
      data-testid="app-topbar"
    >
      {/* Left: company selector */}
      <div className="flex items-center gap-4">
        <XeroLogo size={40} />
        <div className="flex items-center gap-3">
          <button
            data-testid="company-selector"
            className="text-2xl font-semibold text-[#1e2d38] tracking-tight hover:text-[#13B5EA] transition-colors"
          >
            TC Auditors (Pty) Ltd
          </button>
          <button
            data-testid="switch-company-button"
            className="flex items-center gap-1.5 rounded-full border border-[#13B5EA] px-4 py-1.5 text-sm font-medium text-[#13B5EA] hover:bg-[#13B5EA] hover:text-white transition-colors"
          >
            Switch to different company
            <ChevronDown size={15} />
          </button>
        </div>
      </div>

      {/* Right: search + actions */}
      <div className="flex items-center gap-3">
        <div className="relative">
          <Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          />
          <input
            type="text"
            placeholder="Employee Search"
            data-testid="employee-search-input"
            className="h-11 w-64 rounded-full border border-slate-200 bg-slate-50 pl-10 pr-4 text-sm text-slate-700 placeholder:text-slate-400 outline-none transition-colors focus:border-[#13B5EA] focus:bg-white focus:ring-2 focus:ring-[#13B5EA]/20"
          />
        </div>

        <button className={iconBtn} data-testid="notifications-button" aria-label="Notifications">
          <Bell size={21} />
          <span className="absolute top-2.5 right-2.5 h-2 w-2 rounded-full bg-[#13B5EA] ring-2 ring-white" />
        </button>

        <button className={iconBtn} data-testid="support-button" aria-label="Support">
          <HelpCircle size={21} />
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center justify-center h-11 w-11 rounded-full bg-[#13B5EA] text-white font-semibold hover:bg-[#0f9fce] transition-colors"
              data-testid="my-profile-button"
              aria-label="My Profile"
            >
              {user?.name ? user.name.charAt(0).toUpperCase() : <User size={20} />}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="flex flex-col">
              <span className="text-sm font-semibold text-slate-800">{user?.name}</span>
              <span className="text-xs font-normal text-slate-400">{user?.email}</span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => navigate("/")}
              data-testid="menu-my-profile"
              className="cursor-pointer"
            >
              <User size={16} className="mr-2" /> My Profile
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => navigate("/manage-users")}
              data-testid="menu-manage-users"
              className="cursor-pointer"
            >
              <Users size={16} className="mr-2" /> Manage Users
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={logout}
              data-testid="menu-sign-out"
              className="cursor-pointer text-red-600 focus:text-red-600"
            >
              <LogOut size={16} className="mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
